import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const BelfastHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Belfast | Clinical Hypnotherapist in Belfast</title>
        <meta
          name="description"
          content="Professional hypnotherapy serving Belfast from our clinic at Belfast Wellness Clinic on Ormeau Road. Just 10-15 minutes from Belfast city centre. Specializing in anxiety, radiotherapy support, chronic pain & medical anxiety. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Belfast, hypnotherapist Belfast, clinical hypnotherapy Belfast, anxiety treatment Belfast, radiotherapy anxiety Belfast, Ormeau Road hypnotherapy, hypnotherapist near Belfast" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/belfast-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Belfast | Clinical Hypnotherapist in Belfast" />
        <meta property="og:description" content="Professional hypnotherapy in Belfast, Northern Ireland. Specializing in anxiety, radiotherapy support, chronic pain & medical anxiety." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "name": "Sharon Clare Hypnotherapy - Belfast",
            "description": "Professional CNHC registered clinical hypnotherapy services in Belfast, Northern Ireland. Specializing in anxiety, radiotherapy anxiety support, chronic pain management, and medical procedure anxiety.",
            "url": "https://www.sharonclare-hypnotherapy.com/belfast-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "address": {
              "@type": "PostalAddress",
              "name": "Sharon Clare Hypnotherapy - Belfast Clinic",
              "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
              "addressLocality": "Belfast",
              "addressRegion": "County Down",
              "postalCode": "BT7 2FZ",
              "addressCountry": "GB"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "54.5807",
              "longitude": "-5.9186"
            },
            "hasMap": "https://maps.google.com/?q=Belfast+Wellness+Clinic+228+Ormeau+Road+Belfast+BT7+2FZ",
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "areaServed": {
              "@type": "City",
              "name": "Belfast",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Radiotherapy Anxiety Support",
              "Medical Procedure Anxiety",
              "Chronic Pain Management",
              "Stress Management",
              "Trauma Recovery"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy in Belfast
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional Clinical Hypnotherapy Serving Belfast
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              CNHC registered hypnotherapist offering specialized support for anxiety, radiotherapy patients, chronic pain, and medical anxiety from Belfast Wellness Clinic on Ormeau Road, just 10-15 minutes from Belfast city centre.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Belfast Clinic Info */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                  Belfast Clinic Location
                </h2>
                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-6">
                  <div className="flex items-start space-x-4 mb-4">
                    <MapPin className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Sharon Clare Hypnotherapy</h3>
                      <p className="text-gray-700">Belfast Wellness Clinic, 228 Ormeau Road</p>
                      <p className="text-gray-700">Belfast, BT7 2FZ</p>
                      <p className="text-gray-700">Northern Ireland</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 mb-4">
                    <Clock className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-gray-700 font-semibold mb-2">Clinic Hours:</p>
                      <p className="text-gray-700">
                        Appointment times vary week to week. You can see my current availability on the{' '}
                        <a
                          href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-600 hover:text-accent-700 font-medium underline"
                        >
                          clinic booking page
                        </a>
                        .
                      </p>
                      <p className="text-gray-700 text-sm mt-2 italic">Early evening appointments available.</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border-2 border-accent-200 mt-4">
                    <p className="text-gray-900 font-semibold mb-2">
                      <Shield className="w-5 h-5 text-accent-600 inline mr-2" />
                      Just 10-15 Minutes from Belfast City Centre
                    </p>
                    <p className="text-gray-600 text-sm">
                      Convenient south Belfast clinic location on the Ormeau Road, easily accessible from across the city and the surrounding area.
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border-2 border-primary-200 mt-4">
                    <p className="text-gray-900 font-semibold mb-1">Also at Belfast Wellness Clinic</p>
                    <p className="text-gray-600 text-sm mb-2">
                      Sharon is listed as a practitioner at Belfast Wellness Clinic.
                    </p>
                    <a
                      href="https://www.belfastchiropracticclinic.com/sharon-clare/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-600 hover:text-accent-700 text-sm font-medium"
                    >
                      View profile at Belfast Wellness Clinic →
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
                  Why Choose Hypnotherapy in Belfast?
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">CNHC Registered Hypnotherapist</h3>
                      <p className="text-gray-600">Fully qualified and registered with the Complementary and Natural Healthcare Council</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">NHS & Healthcare Experience</h3>
                      <p className="text-gray-600">Volunteer work with Belfast City Hospital Radiotherapy Department and Macmillan Support Centre</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Medical Anxiety Specialist</h3>
                      <p className="text-gray-600">Specialized support for radiotherapy anxiety, surgery fears, MRI anxiety, and medical procedures</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Evidence-Based Approach</h3>
                      <p className="text-gray-600">Solution-focused hypnotherapy based on neuroscience and proven techniques</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Convenient South Belfast Location</h3>
                      <p className="text-gray-600">Easy access from across Belfast, close to the city centre and on good public transport links</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in Belfast */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Hypnotherapy Services in Belfast
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Radiotherapy Anxiety Support
                </h3>
                <p className="text-gray-700 mb-4">
                  Specialized 3-session programme for cancer patients struggling with radiotherapy anxiety or mask intolerance. Drawing on extensive volunteer experience with Belfast City Hospital Radiotherapy Department.
                </p>
                <a href="/radiotherapy-anxiety" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Anxiety & Stress Relief
                </h3>
                <p className="text-gray-700 mb-4">
                  Solution-focused hypnotherapy to reduce anxiety, manage stress, improve sleep, and restore calm. Evidence-based approach for lasting change.
                </p>
                <a href="/anxiety-stress" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Medical Procedure Anxiety
                </h3>
                <p className="text-gray-700 mb-4">
                  Support for surgery fears, MRI anxiety, blood test phobia, and other medical procedures. Feel calm and in control before, during, and after treatment.
                </p>
                <a href="/medical-procedure-anxiety" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Chronic Pain Management
                </h3>
                <p className="text-gray-700 mb-4">
                  Neuroscience-based hypnotherapy to help reduce persistent pain by calming the nervous system and supporting your body's natural healing response.
                </p>
                <a href="/persistent-pain" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>

            <div className="text-center mt-12">
              <a href="/services" className="btn-primary text-lg px-8 py-4 inline-flex items-center">
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Sharon */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Meet Your Belfast Hypnotherapist
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I'm Sharon Clare, a CNHC registered clinical hypnotherapist serving Belfast and Northern Ireland. With specialized experience in medical anxiety and healthcare settings, I offer compassionate, evidence-based hypnotherapy to help you overcome anxiety, manage pain, and navigate medical treatments with confidence.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                My work volunteering with Belfast City Hospital's Radiotherapy Department and Macmillan Support Centre has given me unique insight into supporting patients through challenging medical experiences. I understand the fear, overwhelm, and uncertainty that can come with medical treatment—and I'm here to help you find calm, confidence, and control.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="bg-white rounded-xl p-4">
                  <p className="font-semibold text-gray-900">CNHC Registered</p>
                  <p className="text-sm text-gray-600">Professional Standards</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <p className="font-semibold text-gray-900">NHS Experience</p>
                  <p className="text-sm text-gray-600">Belfast City Hospital</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <p className="font-semibold text-gray-900">100+ Clients</p>
                  <p className="text-sm text-gray-600">Successfully Supported</p>
                </div>
              </div>
            </div>
            <a href="/about" className="btn-secondary text-lg px-8 py-4 inline-flex items-center">
              Learn More About Sharon
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Service Depth Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Specialized Hypnotherapy Services in Belfast
            </h2>
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Radiotherapy Anxiety Support Near Belfast City Hospital
                </h3>
                <p className="text-gray-700 mb-4">
                  Our Belfast clinic's proximity to Belfast City Hospital makes it uniquely positioned to support radiotherapy patients. Many cancer patients experience significant anxiety about radiotherapy treatment, particularly mask intolerance during head and neck radiotherapy. The claustrophobia, loss of control, and repeated treatments can feel overwhelming.
                </p>
                <p className="text-gray-700 mb-4">
                  Through my volunteer work with the Belfast City Hospital Radiotherapy Department and Macmillan Support Centre, I've developed a specialized 3-session programme specifically designed for radiotherapy anxiety. Using solution-focused hypnotherapy and neuroscience-based techniques, we work to calm your nervous system, build coping strategies, and help you feel in control throughout your treatment journey.
                </p>
                <p className="text-gray-700">
                  The clinic sits on the Ormeau Road in south Belfast, a short distance from Belfast City Hospital, so you can plan sessions around your treatment appointments without adding a long journey to an already demanding week.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Pre-Surgery Anxiety and Medical Procedure Support
                </h3>
                <p className="text-gray-700 mb-4">
                  Belfast Trust hospitals perform thousands of surgical procedures annually, and pre-surgery anxiety is incredibly common. Whether you're facing a minor procedure or major surgery at the Royal Victoria Hospital, Mater Hospital, or Belfast City Hospital, the anticipation can trigger significant anxiety, sleep problems, and worry.
                </p>
                <p className="text-gray-700 mb-4">
                  Clinical hypnotherapy can help you approach surgery with calm confidence. We work on reducing anticipatory anxiety, improving sleep before your procedure, managing pain perception, and building mental resilience. Research shows that patients who use hypnotherapy before surgery often experience less post-operative pain, faster recovery times, and reduced anxiety during the procedure itself.
                </p>
                <p className="text-gray-700">
                  Our professional, clinical environment at the Belfast clinic provides the perfect setting for this type of focused, evidence-based therapeutic work. Many Belfast professionals choose hypnotherapy as a complement to their medical care, seeking a science-backed approach to managing surgical anxiety.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Chronic Pain Management in a Clinical Setting
                </h3>
                <p className="text-gray-700 mb-4">
                  Chronic pain affects thousands of people across Belfast and Northern Ireland, often with limited effective treatment options. Whether you're dealing with fibromyalgia, back pain, arthritis, nerve pain, or pain following medical treatment, the impact on your quality of life can be profound.
                </p>
                <p className="text-gray-700 mb-4">
                  Our neuroscience-based approach to pain management uses solution-focused hypnotherapy to help calm your nervous system and reduce pain perception. Pain is processed in the brain, and when we're stressed or anxious, our pain often intensifies. By reducing anxiety and stress, teaching relaxation techniques, and working with your brain's natural pain-gating mechanisms, many clients experience significant improvements in their pain levels and quality of life.
                </p>
                <p className="text-gray-700">
                  The Belfast clinic offers a clinical, professional environment that many clients find reassuring when seeking treatment for medical conditions. With experience supporting patients alongside their medical care, I work as a complementary therapist to enhance your overall pain management strategy.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Work-Related Stress and Professional Anxiety
                </h3>
                <p className="text-gray-700 mb-4">
                  Belfast's professional community faces unique pressures. Whether you work in healthcare, education, finance, law, or any of Belfast's thriving industries, work-related stress and anxiety can significantly impact your performance, wellbeing, and personal life.
                </p>
                <p className="text-gray-700 mb-4">
                  Solution-focused hypnotherapy helps professional clients regain control over stress and anxiety. Unlike traditional talking therapies that focus on problems, our approach focuses on solutions, practical strategies, and building resilience. Many Belfast professionals appreciate the evidence-based, neuroscience-backed approach that fits their logical, results-oriented mindset.
                </p>
                <p className="text-gray-700">
                  Appointment times vary, and early evening slots are available, making it possible to attend around your working day. The discreet, professional environment at our Belfast clinic provides a confidential space to address workplace anxiety, presentation fears, imposter syndrome, or burnout, helping you perform at your best while protecting your mental health.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Belfast Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was terrified about my radiotherapy treatment and couldn't face wearing the mask. After just two sessions with Sharon at the Belfast clinic, I felt completely different. I was able to complete my full course of treatment without panic. Being so close to the City Hospital made it so convenient to fit sessions around my appointments."
                </p>
                <p className="font-semibold text-gray-900">M.K., South Belfast - Radiotherapy Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "As a Belfast professional dealing with severe work anxiety and panic attacks, I needed something that actually worked. Sharon's evidence-based approach made sense to me. After 4 sessions, my anxiety has reduced dramatically and I'm sleeping properly for the first time in years."
                </p>
                <p className="font-semibold text-gray-900">J.D., East Belfast - Anxiety and Stress</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I've lived with chronic pain for over five years and tried everything. Hypnotherapy wasn't something I'd considered, but Sharon's professional, clinical approach convinced me to try it. I'm not pain-free, but my pain levels have reduced significantly and I can do things I couldn't do before."
                </p>
                <p className="font-semibold text-gray-900">T.B., Belfast City Centre - Chronic Pain</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was dreading surgery at the Royal Victoria and my pre-op anxiety was through the roof. Sharon's sessions helped me approach the surgery calmly. The medical team commented on how relaxed I was, and my recovery was much smoother than expected."
                </p>
                <p className="font-semibold text-gray-900">L.W., North Down - Pre-Surgery Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "After years of medical anxiety and avoiding necessary procedures, I finally found help. Sharon understands the medical context and her experience with hospital patients really shows. I've now completed medical procedures I'd been avoiding for years."
                </p>
                <p className="font-semibold text-gray-900">R.M., Lisburn Road - Medical Procedure Anxiety</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Benefits Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Our Belfast Clinic?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Just 10-15 Minutes from Belfast City Centre
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road is a short drive from Belfast city centre, straight down the Ormeau Road. Easily accessible for Belfast residents, the clinic offers a calm, relaxing environment just far enough from the bustle of the city centre.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Easy Access from All Belfast Areas
                </h3>
                <p className="text-gray-700">
                  Located on the Ormeau Road in south Belfast, our clinic is easily accessible from every part of the city. Whether you're travelling from east Belfast, south Belfast, or the city centre, the Ormeau Road and Ormeau Embankment give a direct, straightforward route in. The clinic is also convenient for clients coming from Lisburn and Carryduff via the M1 and A24.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Excellent Public Transport Links
                </h3>
                <p className="text-gray-700">
                  The Ormeau Road is well served by Metro bus routes running to and from Belfast city centre throughout the day, so the clinic is straightforward to reach even if you don't drive. Botanic and City Hospital rail stations are both nearby, with Lanyon Place a short journey away for regional services. For clients fitting appointments around work, the frequency of the Ormeau Road buses makes scheduling flexible.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Professional Clinical Environment
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road provides a professional, clinical environment that many clients find reassuring. The setting reflects the evidence-based, professional approach to hypnotherapy. Conveniently located on the Ormeau Road, it's easily accessible from across Belfast.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Getting Here
                </h3>
                <p className="text-gray-700">
                  The clinic is on the Ormeau Road, a main route in and out of south Belfast, with bus stops close by and Ormeau Park a few minutes away. If you're driving and want to check parking before your first visit, the{' '}
                  <a
                    href="https://www.belfastchiropracticclinic.com/sharon-clare/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-700 font-medium underline"
                  >
                    clinic's own page
                  </a>{' '}
                  has full directions and access details.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Established Healthcare Hub
                </h3>
                <p className="text-gray-700">
                  Being situated in a well-established complementary healthcare clinic means you're entering a space dedicated to wellbeing. The professional reception, quiet treatment rooms, and healthcare-focused environment help you feel at ease from the moment you arrive. Many clients appreciate being in a setting that takes their health concerns seriously.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Belfast Clinic
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is the clinic from Belfast city centre?
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road is around 10 minutes from Belfast city centre, straight down the Ormeau Road. Metro buses run the route regularly, and Botanic and City Hospital stations are both close by, so the journey is straightforward whether you drive or not.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How do I get to the Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  The clinic is at 228 Ormeau Road in south Belfast. Metro bus services run along the Ormeau Road throughout the day, and Botanic and City Hospital rail stations are both within easy reach. If you're driving, have a look at the{' '}
                  <a
                    href="https://www.belfastchiropracticclinic.com/sharon-clare/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-700 font-medium underline"
                  >
                    clinic's page
                  </a>{' '}
                  for directions and parking information before your first visit.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What anxiety conditions do you treat in Belfast?
                </h3>
                <p className="text-gray-700">
                  In our clinic, we commonly treat generalized anxiety disorder, panic attacks, social anxiety, health anxiety, medical procedure anxiety, pre-surgery anxiety, and work-related stress. We also specialize in radiotherapy anxiety, cancer-related anxiety, MRI claustrophobia, and anxiety related to ongoing medical treatment. Our evidence-based, solution-focused approach is effective for a wide range of anxiety conditions.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer evening appointments in Belfast?
                </h3>
                <p className="text-gray-700">
                  Yes — early evening appointments are available at the Belfast clinic, which suits clients who can't get away during the working day. Availability varies from week to week, so the best place to check current times is the{' '}
                  <a
                    href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-700 font-medium underline"
                  >
                    clinic booking page
                  </a>
                  . Online sessions can also be arranged at times that suit your schedule.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is the Belfast clinic wheelchair accessible?
                </h3>
                <p className="text-gray-700">
                  Yes, the clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast has ground floor access and is wheelchair accessible with step-free access to the treatment rooms. If you have any specific accessibility requirements or mobility concerns, please let us know when booking so we can ensure your visit is as comfortable as possible.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How long is a hypnotherapy session in Belfast?
                </h3>
                <p className="text-gray-700">
                  The initial consultation lasts approximately 90 minutes. This extended first session allows time for a thorough assessment, explanation of how solution-focused hypnotherapy works, understanding your goals, and your first hypnotherapy experience. Follow-up sessions are typically 60 minutes. This structure ensures we have adequate time for discussion, therapeutic work, and hypnosis without feeling rushed, helping you get the most from each session.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I claim hypnotherapy on health insurance?
                </h3>
                <p className="text-gray-700">
                  Some private health insurance policies cover hypnotherapy, particularly when provided by a CNHC registered hypnotherapist for recognized conditions like anxiety, pain management, or smoking cessation. Coverage varies significantly between providers and policies. I recommend contacting your insurance provider directly to ask about coverage for CNHC registered hypnotherapy. I can provide receipts and documentation for insurance claims as needed.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you see clients from all over Belfast?
                </h3>
                <p className="text-gray-700">
                  Absolutely. The clinic is on the Ormeau Road in south Belfast, around 10 minutes from the city centre, which makes it convenient for every part of the city. Clients regularly travel from across Belfast as well as Lisburn, Carryduff, Dundonald, Newtownabbey and further afield. Online sessions are also available if travelling is difficult, and there is a second clinic in Lisburn.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What's the success rate for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy has strong evidence supporting its effectiveness for anxiety. While individual results vary, research indicates that hypnotherapy combined with CBT principles is highly effective for reducing anxiety symptoms. Most clients report significant improvements within 4-6 sessions. Our evidence-based, neuroscience-backed approach focuses on practical strategies and measurable change, with many Belfast clients experiencing reduced anxiety, better sleep, and improved coping mechanisms after completing their treatment programme.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe for cancer patients?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is completely safe for cancer patients and can be a valuable complementary support alongside medical treatment. Solution-focused hypnotherapy does not interfere with cancer treatment, medication, or radiotherapy. In fact, through my volunteer work with Belfast City Hospital's Radiotherapy Department, I've supported many cancer patients with radiotherapy anxiety, treatment-related stress, and pain management. Hypnotherapy can help reduce anxiety, improve sleep, manage pain, and support overall wellbeing during cancer treatment. Always inform your medical team about any complementary therapies you're using.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default BelfastHypnotherapy;
